<template>
    <div>
        <!-- Meta setup -->
        <div>
            <Field
                v-model="modelValue.is_seasonal"
                :label-position="'top'"
                :field="{
                    name: lang('fields::fields.opening-hours.is_seasonal'),
                    adminComponent: 'AdminCheckbox',
                }"
            ></Field>
        </div>

        <!-- Seasons -->
        <div v-if="modelValue.is_seasonal">
            <h3 class="font-bold">
                {{ lang('fields::fields.opening-hours.seasons') }}
            </h3>

            <Tabs :tabs="tabs"></Tabs>

            <!-- Season settings -->
            <div v-if="activeSeason !== null">
                <div class="flex justify-end">
                    <Button type="button" @click="switchSeason(null)">
                        {{ lang('general.close') }}
                    </Button>
                </div>

                <div class="space-y-1">
                    <Field
                        v-model="modelValue.seasons[activeSeason].name"
                        :label-position="'top'"
                        :field="{
                            name: lang('fields::fields.opening-hours.season_name'),
                            adminComponent: 'AdminText',
                        }"
                    ></Field>

                    <div class="grid grid-cols-12 gap-4">
                        <div class="grid grid-cols-12 col-span-6 gap-2">
                            <div class="col-span-6">
                                <Field
                                    v-model="modelValue.seasons[activeSeason].start.day"
                                    :label-position="'top'"
                                    :field="{
                                        name: `${lang('fields::fields.opening-hours.season_from')} ${lang(
                                            'fields::fields.opening-hours.start.day',
                                        )}`,
                                        adminComponent: 'AdminNumber',
                                    }"
                                ></Field>
                            </div>
                            <div class="col-span-6">
                                <Field
                                    v-model="modelValue.seasons[activeSeason].start.month"
                                    :label-position="'top'"
                                    :field="{
                                        name: `${lang('fields::fields.opening-hours.season_from')} ${lang(
                                            'fields::fields.opening-hours.start.month',
                                        )}`,
                                        adminComponent: 'AdminNumber',
                                    }"
                                ></Field>
                            </div>
                        </div>
                        <div class="grid grid-cols-12 col-span-6 gap-2">
                            <div class="col-span-6">
                                <Field
                                    v-model="modelValue.seasons[activeSeason].end.day"
                                    :label-position="'top'"
                                    :field="{
                                        name: `${lang('fields::fields.opening-hours.season_to')} ${lang(
                                            'fields::fields.opening-hours.end.day',
                                        )}`,
                                        adminComponent: 'AdminNumber',
                                    }"
                                ></Field>
                            </div>
                            <div class="col-span-6">
                                <Field
                                    v-model="modelValue.seasons[activeSeason].end.month"
                                    :label-position="'top'"
                                    :field="{
                                        name: `${lang('fields::fields.opening-hours.season_to')} ${lang(
                                            'fields::fields.opening-hours.end.month',
                                        )}`,
                                        adminComponent: 'AdminNumber',
                                    }"
                                ></Field>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Opening hours -->
        <table
            class="w-full text-sm border divide-y border-neutral-300 dark:border-neutral-700 divide-neutral-300 dark:divide-neutral-700"
        >
            <thead>
                <tr>
                    <th class="py-2 pl-2 pr-6 text-left bg-neutral-50 dark:bg-neutral-950" width="160">
                        {{ lang('fields::fields.opening-hours.day') }}
                    </th>
                    <th class="px-2 py-2 text-left">
                        {{ lang('fields::fields.opening-hours.shifts') }}
                    </th>
                    <th width="60"></th>
                </tr>
            </thead>
            <tbody class="divide-y divide-neutral-200 dark:divide-neutral-800">
                <tr v-for="(weekday, index) in weekdays" :key="weekday" class="group/row">
                    <th
                        class="py-2 pl-2 pr-6 text-left bg-neutral-50 dark:bg-neutral-950 group-hover/row:bg-neutral-100 group-hover/row:dark:bg-neutral-900"
                        scope="row"
                    >
                        {{ lang(`calendar.weekdays.${index}`) }}
                    </th>
                    <td class="px-2 py-2 space-y-1">
                        <div
                            v-for="(shift, shiftIndex) in activeOpeningHours[weekday]"
                            :key="shiftIndex"
                            class="flex items-stretch space-x-2"
                        >
                            <AdminText
                                v-model="activeOpeningHours[weekday][shiftIndex]"
                                class="flex-auto"
                                :readonly="true"
                                @click="editShift(weekday, shiftIndex)"
                            />
                            <Button type="button" @click="removeShift(activeOpeningHours[weekday], shiftIndex)">
                                <icon icon="hero/solid/minus-sm" class="size-4"></icon>
                            </Button>
                        </div>
                    </td>
                    <td class="px-2 py-2 align-bottom">
                        <Button
                            type="button"
                            class="min-h-[34px]"
                            @click="addShift(activeOpeningHours[weekday], weekday)"
                        >
                            <icon icon="hero/solid/plus-sm" class="size-4"></icon>
                        </Button>
                    </td>
                </tr>
                <tr>
                    <th
                        class="py-2 pl-2 pr-6 text-left bg-neutral-50 dark:bg-neutral-950 group-hover/row:bg-neutral-100 group-hover/row:dark:bg-neutral-900"
                        scope="row"
                    >
                        {{ lang('fields::fields.opening-hours.exceptions') }}
                    </th>
                    <td class="px-2 py-2 space-y-1">
                        <template v-if="activeOpeningHours.exceptions">
                            <div
                                v-for="exceptionDate in Object.keys(activeOpeningHours.exceptions)"
                                :key="exceptionDate"
                            >
                                <div class="flex items-stretch space-x-2">
                                    <div class="flex items-center">
                                        <span>
                                            {{ formatExceptionDate(exceptionDate) }}
                                        </span>
                                    </div>
                                    <div v-if="activeOpeningHours.exceptions[exceptionDate].length === 0">
                                        {{ lang('fields::fields.opening-hours.fullday_off') }}
                                    </div>
                                    <div v-else>
                                        <div
                                            v-for="(exception, shiftIndex) in activeOpeningHours.exceptions[
                                                exceptionDate
                                            ]"
                                            :key="shiftIndex"
                                            class="flex items-stretch space-x-2"
                                        >
                                            <AdminText
                                                v-model="activeOpeningHours.exceptions[exceptionDate][shiftIndex]"
                                                :readonly="true"
                                                class="flex-auto"
                                                @click="editExceptionShift(exceptionDate, shiftIndex)"
                                            />
                                            <Button
                                                type="button"
                                                @click="removeExceptionShift(exceptionDate, shiftIndex)"
                                            >
                                                <icon icon="hero/solid/minus-sm" class="size-4"></icon>
                                            </Button>
                                        </div>
                                    </div>
                                    <Button type="button" @click="removeException(exceptionDate)">
                                        <icon icon="hero/solid/trash" class="size-4"></icon>
                                    </Button>
                                </div>
                            </div>
                        </template>
                    </td>
                    <td class="px-2 py-2 align-bottom">
                        <Button type="button" class="min-h-[34px]" @click="addException()">
                            <icon icon="hero/solid/plus-sm" class="size-4"></icon>
                        </Button>
                    </td>
                </tr>
            </tbody>
        </table>

        <Modal :show="showEditShiftModal" @close="closeEditShiftModal">
            <div class="space-y-2">
                <div>
                    <h3 class="font-bold">{{ lang('fields::fields.opening-hours.edit_shift') }}</h3>
                </div>
                <div class="grid grid-cols-12">
                    <div class="grid grid-cols-12 col-span-5">
                        <div class="col-span-5">
                            <AdminNumber v-model="editData.start.hour" min="0" max="24" />
                        </div>
                        <div class="col-span-2 text-center">:</div>
                        <div class="col-span-5">
                            <AdminNumber v-model="editData.start.minute" min="0" max="59" />
                        </div>
                    </div>
                    <div class="col-span-2 text-center">&mdash;</div>
                    <div class="grid grid-cols-12 col-span-5">
                        <div class="col-span-5">
                            <AdminNumber v-model="editData.end.hour" min="0" max="24" />
                        </div>
                        <div class="col-span-2 text-center">:</div>
                        <div class="col-span-5">
                            <AdminNumber v-model="editData.end.minute" min="0" max="59" />
                        </div>
                    </div>
                </div>
                <div class="flex justify-center">
                    <Button type="button" color="primary" @click="saveEditShift">
                        {{ lang('general.save') }}
                    </Button>
                </div>
            </div>
        </Modal>

        <Modal :show="showEditExceptionModal" @close="closeEditExceptionModal">
            <div class="space-y-2">
                <div>
                    <h3 class="font-bold">{{ lang('fields::fields.opening-hours.edit_exception') }}</h3>
                </div>
                <div class="grid grid-cols-12">
                    <div class="grid grid-cols-12 col-span-5">
                        <div class="col-span-5">
                            <AdminNumber v-model="editExceptionData.start.hour" min="0" max="24" />
                        </div>
                        <div class="col-span-2 text-center">:</div>
                        <div class="col-span-5">
                            <AdminNumber v-model="editExceptionData.start.minute" min="0" max="59" />
                        </div>
                    </div>
                    <div class="col-span-2 text-center">&mdash;</div>
                    <div class="grid grid-cols-12 col-span-5">
                        <div class="col-span-5">
                            <AdminNumber v-model="editExceptionData.end.hour" min="0" max="24" />
                        </div>
                        <div class="col-span-2 text-center">:</div>
                        <div class="col-span-5">
                            <AdminNumber v-model="editExceptionData.end.minute" min="0" max="59" />
                        </div>
                    </div>
                </div>
                <div class="flex justify-center">
                    <Button type="button" color="primary" @click="saveEditExceptionShift">
                        {{ lang('general.save') }}
                    </Button>
                </div>
            </div>
        </Modal>

        <Modal :show="showExceptionModal" @close="closeExceptionModal">
            <div class="space-y-2">
                <div>
                    <h3 class="font-bold">{{ lang('fields::fields.opening-hours.add_exception') }}</h3>
                </div>
                <div class="grid items-center grid-cols-12 gap-5">
                    <div class="col-span-4">
                        <AdminDate v-model="exceptionData.date" />
                    </div>
                    <div class="col-span-4">
                        <label class="flex items-center space-x-2">
                            <AdminCheckbox v-model="exceptionData.every_year" />
                            <span>
                                {{ lang('fields::fields.opening-hours.every_year') }}
                            </span>
                        </label>
                    </div>
                    <div class="col-span-4">
                        <label class="flex items-center space-x-2">
                            <AdminCheckbox v-model="exceptionData.fullday_off" />
                            <span>
                                {{ lang('fields::fields.opening-hours.fullday_off') }}
                            </span>
                        </label>
                    </div>
                </div>
                <div class="grid items-center grid-cols-12 gap-5">
                    <div class="grid items-center grid-cols-12 col-span-5">
                        <div class="col-span-5">
                            <AdminNumber v-model="exceptionData.start.hour" min="0" max="24" />
                        </div>
                        <div class="col-span-2 text-center">:</div>
                        <div class="col-span-5">
                            <AdminNumber v-model="exceptionData.start.minute" min="0" max="59" />
                        </div>
                    </div>
                    <div class="col-span-2 text-center">&mdash;</div>
                    <div class="grid items-center grid-cols-12 col-span-5">
                        <div class="col-span-5">
                            <AdminNumber v-model="exceptionData.end.hour" min="0" max="24" />
                        </div>
                        <div class="col-span-2 text-center">:</div>
                        <div class="col-span-5">
                            <AdminNumber v-model="exceptionData.end.minute" min="0" max="59" />
                        </div>
                    </div>
                </div>
                <div class="flex justify-center">
                    <Button type="button" color="primary" @click="saveException">
                        {{ lang('general.save') }}
                    </Button>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import type { Ref } from 'vue';
import { lang } from '@/lang/composable';
import type { FieldComponent } from '@src/Common/Components/types';
import type { OpeningHours, SeasonalOpeningHours, OpeningSeason } from '@src/Fields/Pages/types';

const defaultOpeningHours: OpeningHours | SeasonalOpeningHours = {
    is_seasonal: false,
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
    exceptions: {},
};

const emit = defineEmits(['update:modelValue']);

const weekdays: string[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const props = withDefaults(defineProps<FieldComponent & { modelValue?: OpeningHours | SeasonalOpeningHours }>(), {
    modelValue: null,
});

const modelValue = ref(props.modelValue ?? defaultOpeningHours);

const getActiveOpeningHours = () => {
    if (modelValue.value.is_seasonal && activeSeason.value !== null) {
        return modelValue.value.seasons[activeSeason.value].opening_hours;
    }

    return modelValue.value;
};

const activeOpeningHours = computed(() => getActiveOpeningHours());

/**
 * Shifts
 */
const addShift = (shifts: string[], weekday: string) => {
    const oh = getActiveOpeningHours();
    if (!oh[weekday]) {
        oh[weekday] = [];
        shifts = oh[weekday];
    }

    shifts.push('00:00-00:00');
    modelValue.value = { ...modelValue.value };
};

const removeShift = (shifts: string[], index: number) => {
    shifts.splice(index, 1);
    modelValue.value = { ...modelValue.value };
};

const showEditShiftModal = ref(false);
const editShiftWeekday = ref('');
const editShiftIndex = ref(0);

const editData = ref({
    start: {
        hour: 0,
        minute: 0,
    },
    end: {
        hour: 0,
        minute: 0,
    },
});

const openEditShiftModal = () => {
    showEditShiftModal.value = true;
};

const closeEditShiftModal = () => {
    showEditShiftModal.value = false;
};

const editShift = (weekday: string, index: number) => {
    const oh = getActiveOpeningHours();
    editShiftWeekday.value = weekday;
    editShiftIndex.value = index;

    const [start, end] = oh[weekday][index].split('-');
    editData.value.start = {
        hour: parseInt(start.split(':')[0], 10),
        minute: parseInt(start.split(':')[1], 10),
    };
    editData.value.end = {
        hour: parseInt(end.split(':')[0], 10),
        minute: parseInt(end.split(':')[1], 10),
    };

    openEditShiftModal();
};

const saveEditShift = () => {
    const oh = getActiveOpeningHours();
    const startHour = editData.value.start.hour.toString().padStart(2, '0');
    const startMinute = editData.value.start.minute.toString().padStart(2, '0');
    const endHour = editData.value.end.hour.toString().padStart(2, '0');
    const endMinute = editData.value.end.minute.toString().padStart(2, '0');

    oh[editShiftWeekday.value][editShiftIndex.value] = `${startHour}:${startMinute}-${endHour}:${endMinute}`;
    modelValue.value = { ...modelValue.value };

    closeEditShiftModal();
};

/**
 * Exceptions
 */
const showExceptionModal = ref(false);

const exceptionData = ref({
    date: null,
    fullday_off: false,
    every_year: false,
    start: {
        hour: 0,
        minute: 0,
    },
    end: {
        hour: 0,
        minute: 0,
    },
});

const closeExceptionModal = () => {
    showExceptionModal.value = false;
};

const openExceptionModal = () => {
    showExceptionModal.value = true;
};

const saveException = () => {
    const oh = getActiveOpeningHours();
    let date = exceptionData.value.date;

    // Extrahovat pouze YYYY-MM-DD část, pokud obsahuje čas
    if (date && date.includes('T')) {
        date = date.split('T')[0];
    }

    if (exceptionData.value.every_year) {
        const [, month, day] = date.split('-');
        date = `${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    if (!oh.exceptions) {
        oh.exceptions = {};
    }

    if (exceptionData.value.fullday_off) {
        oh.exceptions = {
            ...oh.exceptions,
            [date]: [],
        };
    } else {
        const startHour = exceptionData.value.start.hour.toString().padStart(2, '0');
        const startMinute = exceptionData.value.start.minute.toString().padStart(2, '0');
        const endHour = exceptionData.value.end.hour.toString().padStart(2, '0');
        const endMinute = exceptionData.value.end.minute.toString().padStart(2, '0');
        const value = `${startHour}:${startMinute}-${endHour}:${endMinute}`;

        oh.exceptions = {
            ...oh.exceptions,
            [date]: oh.exceptions[date] ? [...oh.exceptions[date], value] : [value],
        };
    }

    modelValue.value = { ...modelValue.value };
    closeExceptionModal();
};

const addException = () => {
    openExceptionModal();
};

const formatExceptionDate = (dateString: string): string => {
    const parts = dateString.split('-');

    if (parts.length === 2) {
        // Format MM-DD (recurring yearly)
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        return `${day}.${month}.`;
    } else if (parts.length === 3) {
        // Format YYYY-MM-DD (one-time)
        const day = parseInt(parts[2], 10);
        const month = parseInt(parts[1], 10);
        const year = parts[0];
        return `${day}.${month}.${year}`;
    }

    return dateString;
};

const removeExceptionShift = (exceptionDate, shiftIndex) => {
    const oh = getActiveOpeningHours();
    oh.exceptions[exceptionDate].splice(shiftIndex, 1);
    oh.exceptions = { ...oh.exceptions };
    modelValue.value = { ...modelValue.value };
};

const removeException = (exceptionDate) => {
    const oh = getActiveOpeningHours();
    const exceptionsCopy = { ...oh.exceptions };
    delete exceptionsCopy[exceptionDate];
    oh.exceptions = exceptionsCopy;
    modelValue.value = { ...modelValue.value };
};

/**
 * Edit exception shift
 */
const showEditExceptionModal = ref(false);
const editExceptionDate = ref('');
const editExceptionShiftIndex = ref(0);

const editExceptionData = ref({
    start: {
        hour: 0,
        minute: 0,
    },
    end: {
        hour: 0,
        minute: 0,
    },
});

const openEditExceptionModal = () => {
    showEditExceptionModal.value = true;
};

const closeEditExceptionModal = () => {
    showEditExceptionModal.value = false;
};

const editExceptionShift = (exceptionDate, shiftIndex) => {
    const oh = getActiveOpeningHours();
    editExceptionDate.value = exceptionDate;
    editExceptionShiftIndex.value = shiftIndex;

    const shift = oh.exceptions[exceptionDate][shiftIndex];
    const [start, end] = shift.split('-');

    editExceptionData.value.start = {
        hour: parseInt(start.split(':')[0], 10),
        minute: parseInt(start.split(':')[1], 10),
    };

    editExceptionData.value.end = {
        hour: parseInt(end.split(':')[0], 10),
        minute: parseInt(end.split(':')[1], 10),
    };

    openEditExceptionModal();
};

const saveEditExceptionShift = () => {
    const oh = getActiveOpeningHours();
    const startHour = editExceptionData.value.start.hour.toString().padStart(2, '0');
    const startMinute = editExceptionData.value.start.minute.toString().padStart(2, '0');
    const endHour = editExceptionData.value.end.hour.toString().padStart(2, '0');
    const endMinute = editExceptionData.value.end.minute.toString().padStart(2, '0');

    const newShift = `${startHour}:${startMinute}-${endHour}:${endMinute}`;

    oh.exceptions[editExceptionDate.value][editExceptionShiftIndex.value] = newShift;
    oh.exceptions = { ...oh.exceptions };

    modelValue.value = { ...modelValue.value };
    closeEditExceptionModal();
};

/**
 * Seasons management
 */
const activeSeason: Ref<number | null> = ref(null);
const defaultOpeningSeason: OpeningSeason = {
    name: null,
    start: {
        day: null,
        month: null,
    },
    end: {
        day: null,
        month: null,
    },
    opening_hours: {
        monday: defaultOpeningHours.monday,
        tuesday: defaultOpeningHours.tuesday,
        wednesday: defaultOpeningHours.wednesday,
        thursday: defaultOpeningHours.thursday,
        friday: defaultOpeningHours.friday,
        saturday: defaultOpeningHours.saturday,
        sunday: defaultOpeningHours.sunday,
        exceptions: defaultOpeningHours.exceptions,
    },
};

const addSeason = () => {
    const name = prompt(lang('fields::fields.opening-hours.season_name'));

    if (!name) {
        return;
    }

    const season = {
        ...defaultOpeningSeason,
        name,
    };

    modelValue.value.seasons = modelValue.value.seasons ?? [];
    modelValue.value.seasons.push(season);
    modelValue.value = { ...modelValue.value };

    fillTabs();
};

const switchSeason = (seasonIndex: number | null) => {
    activeSeason.value = seasonIndex;
};

const tabs = ref([]);

const fillTabs = () => {
    tabs.value = [
        {
            name: lang('fields::fields.opening-hours.general_opening_season'),
            current: () => activeSeason.value === null,
            click: () => switchSeason(null),
        },
    ];

    if (modelValue.value.seasons && modelValue.value.seasons.length > 0) {
        modelValue.value.seasons.forEach((season, index) => {
            tabs.value.push({
                name: season.name,
                current: () => activeSeason.value === index,
                click: () => switchSeason(index),
            });
        });
    }

    tabs.value.push({
        name: lang('fields::fields.opening-hours.add_season'),
        current: () => false,
        click: () => addSeason(),
    });
};

fillTabs();

/**
 * Update model value
 */
watch(
    modelValue,
    () => {
        emit('update:modelValue', modelValue.value);
    },
    { deep: true },
);
</script>
